import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { UpdateAddressContactDto, useUpdateContact } from '../../../../../api/created/addressContact.ts';
import {
  useAddressContactsList,
  useCreateAddressContact,
  useSetAddressContactStatus,
} from '../../../../../api/generated/address-controller/address-controller.ts';
import { AddressContactDto } from '../../../../../api/generated/model/addressContactDto.ts';
import { queryClient } from '../../../../../api/queryClient.ts';
import { CreateAddressContactDialogResult } from '../../../../components/patient-address/CreateAddressContactDialog.tsx';

const CONTACTS_QUERY_KEY = ['/api/addresses/contacts'];
const DEBOUNCE_MS = 300;

export function useContactPersonTable() {
  const [page, setPage] = useState(0);
  const size = 20;
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, DEBOUNCE_MS);
    return () => clearTimeout(timeout);
  }, [search]);

  const { data, isLoading, isFetching } = useAddressContactsList({
    page,
    size,
    search: debouncedSearch || undefined,
  });

  const invalidateContacts = async () => {
    await queryClient.invalidateQueries({ queryKey: CONTACTS_QUERY_KEY });
  };

  const setContactStatusMutation = useSetAddressContactStatus({
    mutation: {
      onSuccess: invalidateContacts,
      onError: () => {
        toast.error('Beim (De-)Aktivieren des Ansprechpersonen ist ein Fehler aufgetreten');
      },
    },
  });

  const createContactMutation = useCreateAddressContact({
    mutation: {
      onSuccess: async () => {
        await invalidateContacts();
        toast.success('Ansprechperson erfolgreich erstellt');
      },
      onError: () => {
        toast.error('Beim Erstellen des Ansprechpersonen ist ein Fehler aufgetreten');
      },
    },
  });

  const updateContactMutation = useUpdateContact({
    mutation: {
      onSuccess: async () => {
        await invalidateContacts();
        toast.success('Ansprechperson erfolgreich bearbeitet');
      },
      onError: () => {
        toast.error('Beim Bearbeiten des Ansprechpersonen ist ein Fehler aufgetreten');
      },
    },
  });

  const contacts = data?.content ?? [];
  const totalCount = data?.totalCount ?? 0;
  const currentPage = data?.page ?? page;
  const currentSize = data?.size ?? size;
  const totalPages = currentSize > 0 ? Math.ceil(totalCount / currentSize) : 0;

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(0);
  };

  const handleToggleActive = (contact: AddressContactDto) => {
    if (!contact.id) return;

    setContactStatusMutation.mutate({
      id: contact.id,
      params: {
        isActive: !contact.isActive,
      },
    });
  };

  const handleCreate = (result: CreateAddressContactDialogResult) => {
    createContactMutation.mutate({
      id: result.addressId,
      data: result.contactDto as AddressContactDto,
    });
  };

  const handleUpdate = (result: CreateAddressContactDialogResult) => {
    const data = result.contactDto as UpdateAddressContactDto;
    if (!data.id) return;

    // Contact-centric: keyed by contact id, addresses carried in the body's addressIds.
    updateContactMutation.mutate({ id: data.id, data });
  };

  return {
    isLoading: isLoading || isFetching,
    page: currentPage,
    totalPages,
    search,
    setSearch: handleSearchChange,
    setPage,
    contacts,
    handleToggleActive,
    handleCreate,
    handleUpdate,
    isTogglePending: setContactStatusMutation.isPending,
    isCreatePending: createContactMutation.isPending,
    isUpdatePending: updateContactMutation.isPending,
  };
}
